/**
 * HudControls: fixed bottom-right touch controls for gameplay.
 *
 * Always-on buttons (shown during play, hidden while the editor is open):
 *   - camera view: first-person / third-person toggle.
 *   - fly/roam: toggles the first-person movement mode.
 *   - run/walk: flips a persistent run flag on InputController.
 *   - jump: tap to request a one-shot jump (edge-triggered, same as Space).
 *
 * These mirror the keyboard (Shift / Space) so touch users get the same input.
 */

import type { InputController } from '../input/input-controller';

type HudViewMode = 'first' | 'third';
type HudMoveMode = 'fly' | 'walk';
type HudAvatarAction = { name: string; label: string };

interface HudVideoState {
    id: string;
    paused: boolean;
    muted: boolean;
    currentTime: number;
    duration: number;
    canSeek: boolean;
}

interface HudControlsOptions {
    onToggleView: () => void;
    onToggleMoveMode: () => void;
    onAvatarAction: (name: string) => void;
    onVideoShow: () => void;
    onVideoHide: () => void;
    onVideoPlayPause: () => void;
    onVideoMuteToggle: () => void;
    onVideoSeek: (time: number) => void;
    getViewMode: () => HudViewMode;
    getMoveMode: () => HudMoveMode;
    getVideoState: () => HudVideoState | null;
    actionAnims: ReadonlyArray<HudAvatarAction>;
}

class HudControls {
    private _el: HTMLElement;
    private _actionsRoot: HTMLElement;
    private _actionsEl: HTMLElement;
    private _videoRoot: HTMLElement;
    private _videoPanel: HTMLElement;
    private _input: InputController;
    private _options: HudControlsOptions;
    private _viewBtn: HTMLButtonElement;
    private _modeBtn: HTMLButtonElement;
    private _runBtn: HTMLButtonElement;
    private _actionToggleBtn: HTMLButtonElement;
    private _videoToggleBtn: HTMLButtonElement;
    private _videoPlayBtn: HTMLButtonElement;
    private _videoMuteBtn: HTMLButtonElement;
    private _videoCloseBtn: HTMLButtonElement;
    private _videoProgress: HTMLInputElement;
    private _videoCurrent: HTMLSpanElement;
    private _videoDuration: HTMLSpanElement;
    private _actionsOpen = false;
    private _videoOpen = false;
    private _videoDragging = false;
    private _collapsed = false;
    private _collapseBtn: HTMLButtonElement;

    constructor(input: InputController, options: HudControlsOptions) {
        this._input = input;
        this._options = options;
        this._el = document.createElement('div');
        this._el.id = 'hud-controls';
        this._el.className = 'hidden';

        this._viewBtn = document.createElement('button');
        this._viewBtn.id = 'hud-view';
        this._viewBtn.className = 'hud-btn';
        this._viewBtn.type = 'button';
        this._viewBtn.addEventListener('click', () => {
            this._options.onToggleView();
            this.sync();
        });

        this._modeBtn = document.createElement('button');
        this._modeBtn.id = 'hud-mode';
        this._modeBtn.className = 'hud-btn';
        this._modeBtn.type = 'button';
        this._modeBtn.addEventListener('click', () => {
            this._options.onToggleMoveMode();
            this.sync();
        });

        this._runBtn = document.createElement('button');
        this._runBtn.id = 'hud-run';
        this._runBtn.className = 'hud-btn';
        this._runBtn.type = 'button';
        this._runBtn.textContent = '走';
        this._runBtn.setAttribute('aria-pressed', 'false');
        this._runBtn.addEventListener('click', () => this._toggleRun());

        const jumpBtn = document.createElement('button');
        jumpBtn.id = 'hud-jump';
        jumpBtn.className = 'hud-btn hud-jump';
        jumpBtn.type = 'button';
        jumpBtn.textContent = '跳';
        // pointerdown for snappy feel + so a quick tap registers before any scroll.
        jumpBtn.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            this._input.requestJump();
            jumpBtn.classList.add('firing');
        });
        jumpBtn.addEventListener('pointerup', () => jumpBtn.classList.remove('firing'));
        jumpBtn.addEventListener('pointerleave', () => jumpBtn.classList.remove('firing'));

        this._collapseBtn = document.createElement('button');
        this._collapseBtn.id = 'hud-collapse';
        this._collapseBtn.className = 'hud-collapse-btn';
        this._collapseBtn.type = 'button';
        this._collapseBtn.textContent = '›';
        this._collapseBtn.title = '收起控制按钮';
        this._collapseBtn.setAttribute('aria-label', '收起控制按钮');
        this._collapseBtn.setAttribute('aria-expanded', 'true');
        this._collapseBtn.addEventListener('click', () => this._toggleCollapse());

        const primary = document.createElement('div');
        primary.id = 'hud-primary-controls';
        primary.appendChild(this._viewBtn);
        primary.appendChild(this._modeBtn);
        primary.appendChild(this._runBtn);
        primary.appendChild(jumpBtn);
        this._el.appendChild(this._collapseBtn);
        this._el.appendChild(primary);

        this._actionToggleBtn = document.createElement('button');
        this._actionToggleBtn.id = 'hud-action-toggle';
        this._actionToggleBtn.className = 'hud-panel-toggle';
        this._actionToggleBtn.type = 'button';
        this._actionToggleBtn.textContent = '人物动作';
        this._actionToggleBtn.addEventListener('click', () => this._toggleActions());

        this._actionsEl = this._renderActionButtons();
        this._actionsRoot = document.createElement('div');
        this._actionsRoot.id = 'hud-actions-panel';
        this._actionsRoot.className = 'hidden';
        this._actionsRoot.appendChild(this._actionToggleBtn);
        this._actionsRoot.appendChild(this._actionsEl);

        this._videoToggleBtn = document.createElement('button');
        this._videoToggleBtn.id = 'hud-video-toggle';
        this._videoToggleBtn.className = 'hud-panel-toggle';
        this._videoToggleBtn.type = 'button';
        this._videoToggleBtn.textContent = '视频控制';
        this._videoToggleBtn.addEventListener('click', () => this._toggleVideoPanel());

        this._videoPlayBtn = document.createElement('button');
        this._videoPlayBtn.id = 'hud-video-play';
        this._videoPlayBtn.className = 'hud-video-btn';
        this._videoPlayBtn.type = 'button';
        this._videoPlayBtn.addEventListener('click', () => {
            this._options.onVideoPlayPause();
            this._syncVideoControls();
        });

        this._videoMuteBtn = document.createElement('button');
        this._videoMuteBtn.id = 'hud-video-mute';
        this._videoMuteBtn.className = 'hud-video-btn';
        this._videoMuteBtn.type = 'button';
        this._videoMuteBtn.addEventListener('click', () => {
            this._options.onVideoMuteToggle();
            this._syncVideoControls();
        });

        this._videoCurrent = document.createElement('span');
        this._videoCurrent.id = 'hud-video-current';
        this._videoCurrent.className = 'hud-video-time';

        this._videoProgress = document.createElement('input');
        this._videoProgress.id = 'hud-video-progress';
        this._videoProgress.type = 'range';
        this._videoProgress.min = '0';
        this._videoProgress.step = '0.01';
        this._videoProgress.addEventListener('pointerdown', () => { this._videoDragging = true; });
        this._videoProgress.addEventListener('input', () => {
            this._videoDragging = true;
            const state = this._options.getVideoState();
            this._setVideoTimeText(Number(this._videoProgress.value), state?.duration ?? 0);
        });
        this._videoProgress.addEventListener('change', () => {
            this._options.onVideoSeek(Number(this._videoProgress.value));
            this._videoDragging = false;
            this._syncVideoControls();
        });
        this._videoProgress.addEventListener('pointerup', () => {
            this._options.onVideoSeek(Number(this._videoProgress.value));
            this._videoDragging = false;
            this._syncVideoControls();
        });

        this._videoDuration = document.createElement('span');
        this._videoDuration.id = 'hud-video-duration';
        this._videoDuration.className = 'hud-video-time';

        this._videoCloseBtn = document.createElement('button');
        this._videoCloseBtn.id = 'hud-video-close';
        this._videoCloseBtn.className = 'hud-video-btn';
        this._videoCloseBtn.type = 'button';
        this._videoCloseBtn.textContent = '隐藏';
        this._videoCloseBtn.title = '隐藏视频面板';
        this._videoCloseBtn.setAttribute('aria-label', '隐藏视频面板');
        this._videoCloseBtn.addEventListener('click', () => {
            this._options.onVideoHide();
            this._videoOpen = false;
            this._syncVideoControls();
        });

        this._videoPanel = document.createElement('div');
        this._videoPanel.id = 'hud-video-panel';
        this._videoPanel.className = 'hidden';
        this._videoPanel.appendChild(this._videoPlayBtn);
        this._videoPanel.appendChild(this._videoMuteBtn);
        this._videoPanel.appendChild(this._videoCurrent);
        this._videoPanel.appendChild(this._videoProgress);
        this._videoPanel.appendChild(this._videoDuration);
        this._videoPanel.appendChild(this._videoCloseBtn);

        this._videoRoot = document.createElement('div');
        this._videoRoot.id = 'hud-video-controls';
        this._videoRoot.className = 'hidden';
        this._videoRoot.appendChild(this._videoToggleBtn);
        this._videoRoot.appendChild(this._videoPanel);

        const hudLayer = document.getElementById('ui-layer') ?? document.body;
        hudLayer.appendChild(this._el);
        hudLayer.appendChild(this._actionsRoot);
        hudLayer.appendChild(this._videoRoot);
        this.sync();
    }

    private _renderActionButtons(): HTMLElement {
        const group = document.createElement('div');
        group.id = 'hud-actions';
        for (const action of this._options.actionAnims) {
            const btn = document.createElement('button');
            btn.className = 'hud-action-btn';
            btn.type = 'button';
            btn.textContent = action.label;
            btn.title = action.label;
            btn.setAttribute('aria-label', action.label);
            btn.addEventListener('click', () => this._options.onAvatarAction(action.name));
            group.appendChild(btn);
        }
        return group;
    }

    setActions(actions: ReadonlyArray<HudAvatarAction>): void {
        this._options.actionAnims = actions;
        const next = this._renderActionButtons();
        this._actionsEl.replaceWith(next); this._actionsEl = next; this._syncActionControls();
    }

    private _toggleActions(): void {
        this._actionsOpen = !this._actionsOpen;
        this._syncActionControls();
    }

    private _toggleVideoPanel(): void {
        if (!this._options.getVideoState()) return;
        if (!this._videoOpen) this._options.onVideoShow();
        this._videoOpen = !this._videoOpen;
        this._syncVideoControls();
    }

    private _toggleRun(): void {
        const on = !this._input.runToggle;
        this._input.setRunToggle(on);
        this._runBtn.textContent = on ? '跑' : '走';
        this._runBtn.classList.toggle('active', on);
        this._runBtn.setAttribute('aria-pressed', String(on));
    }

    private _toggleCollapse(): void {
        this._collapsed = !this._collapsed;
        this._el.classList.toggle('collapsed', this._collapsed);
        this._collapseBtn.textContent = this._collapsed ? '‹' : '›';
        this._collapseBtn.title = this._collapsed ? '展开控制按钮' : '收起控制按钮';
        this._collapseBtn.setAttribute('aria-label', this._collapseBtn.title);
        this._collapseBtn.setAttribute('aria-expanded', String(!this._collapsed));
    }

    sync(): void {
        const viewMode = this._options.getViewMode();
        const moveMode = this._options.getMoveMode();
        const third = viewMode === 'third';
        const walking = moveMode === 'walk';

        this._viewBtn.textContent = third ? '三' : '一';
        this._viewBtn.title = third ? '第三人称，点击切换第一人称' : '第一人称，点击切换第三人称';
        this._viewBtn.setAttribute('aria-label', this._viewBtn.title);
        this._viewBtn.setAttribute('aria-pressed', String(third));
        this._viewBtn.classList.toggle('active', third);

        this._modeBtn.textContent = walking ? '游' : '飞';
        this._modeBtn.title = walking ? '漫游模式，点击切换飞行' : '飞行模式，点击切换漫游';
        this._modeBtn.setAttribute('aria-label', this._modeBtn.title);
        this._modeBtn.setAttribute('aria-pressed', String(walking));
        this._modeBtn.classList.toggle('active', walking);

        this._syncActionControls();
        this._syncVideoControls();
    }

    update(): void {
        this._syncVideoControls();
    }

    private _syncActionControls(): void {
        this._actionToggleBtn.setAttribute('aria-expanded', String(this._actionsOpen));
        this._actionToggleBtn.classList.toggle('active', this._actionsOpen);
        this._actionsEl.classList.toggle('hidden', !this._actionsOpen);
    }

    private _syncVideoControls(): void {
        const state = this._options.getVideoState();
        const disabled = !state;
        this._videoToggleBtn.disabled = disabled;
        this._videoToggleBtn.classList.toggle('disabled', disabled);
        if (disabled) this._videoOpen = false;
        this._videoToggleBtn.setAttribute('aria-expanded', String(this._videoOpen));
        this._videoToggleBtn.classList.toggle('active', this._videoOpen);
        this._videoPanel.classList.toggle('hidden', !this._videoOpen);

        const controlDisabled = disabled || !state?.canSeek;
        this._videoPlayBtn.disabled = disabled;
        this._videoMuteBtn.disabled = disabled;
        this._videoProgress.disabled = controlDisabled;
        this._videoCloseBtn.disabled = disabled;

        if (!state) {
            this._videoPlayBtn.textContent = '>';
            this._videoMuteBtn.textContent = 'M';
            this._videoProgress.max = '0';
            this._videoProgress.value = '0';
            this._setVideoTimeText(0, 0);
            return;
        }

        this._videoPlayBtn.textContent = state.paused ? '>' : '||';
        this._videoMuteBtn.textContent = state.muted ? 'M' : 'S';
        this._videoPlayBtn.title = state.paused ? 'Play' : 'Pause';
        this._videoMuteBtn.title = state.muted ? 'Muted' : 'Sound on';
        this._videoProgress.max = String(state.duration);
        if (!this._videoDragging) this._videoProgress.value = String(state.currentTime);
        this._setVideoTimeText(
            this._videoDragging ? Number(this._videoProgress.value) : state.currentTime,
            state.duration
        );
    }

    private _setVideoTimeText(current: number, duration: number): void {
        this._videoCurrent.textContent = formatTime(current);
        this._videoDuration.textContent = formatTime(duration);
    }

    /** Show during play, hide while the editor is open. */
    setVisible(editorActive: boolean): void {
        if (!editorActive) this.sync();
        this._el.classList.toggle('hidden', editorActive);
        this._actionsRoot.classList.toggle('hidden', editorActive);
        this._videoRoot.classList.toggle('hidden', editorActive);
        if (editorActive) {
            this._actionsOpen = false;
            this._videoOpen = false;
            this._syncActionControls();
            this._syncVideoControls();
        }
    }

    destroy(): void {
        this._el.remove();
        this._actionsRoot.remove();
        this._videoRoot.remove();
    }
}

function formatTime(value: number): string {
    if (!Number.isFinite(value) || value <= 0) return '0:00';
    const total = Math.floor(value);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export { HudControls };
